// ============================================
// NRS.01 — Captain Result Listener Types
// ============================================
// Schema chain: CaptainResultDocument (CAP.17) → LabResultInjection
// Contract: Consumes Firestore onSnapshot, validates, routes by status
// ============================================

import type { CaptainResultDocument, CaptainResponse, ExtractionStatus } from '../../shared/captainPipeline';

// ── Listener Configuration ──

export interface CaptainListenerConfig {
  /** Firestore collection path for captain results */
  collectionPath: string;
  /** Patient ID to filter onSnapshot */
  patientId: string;
  /** Current session ID */
  sessionId: string;
  /** Health check threshold in ms (default: 5 minutes) */
  healthCheckThresholdMs: number;
  /** Whether to enable clinical sanity checks */
  enableSanityCheck: boolean;
}

export const DEFAULT_LISTENER_CONFIG: Omit<CaptainListenerConfig, 'patientId' | 'sessionId'> = {
  collectionPath: 'captain_results',
  healthCheckThresholdMs: 5 * 60 * 1000,
  enableSanityCheck: true,
};

// ── Schema Validation ──

export interface SchemaValidationResult {
  valid: boolean;
  errors: SchemaValidationError[];
  /** The validated document, null if invalid */
  document: CaptainResultDocument | null;
}

export interface SchemaValidationError {
  field: string;
  expected: string;
  actual: string;
  message: string;
}

// ── Clinical Sanity Check ──

export interface SanityCheckRange {
  biomarkerName: string;
  min: number;
  max: number;
  unit: string;
}

export interface SanityCheckResult {
  passed: boolean;
  failures: SanityCheckFailure[];
}

export interface SanityCheckFailure {
  biomarkerName: string;
  value: number;
  unit: string;
  expectedRange: { min: number; max: number };
  message: string;
}

// ── Lab Result Injection ──

export interface LabResultInjection {
  /** Session ID from CaptainResultDocument */
  sessionId: string;
  /** Patient ID from CaptainResultDocument */
  patientId: string;
  /** Full captain response (validated, sanity checked) */
  captainResponse: CaptainResponse;
  /** Extraction status from upstream */
  extractionStatus: ExtractionStatus;
  /** Whether this is a partial result set */
  isPartial: boolean;
  /** Failure reason (only when extractionStatus === 'failed') */
  failureReason?: string;
  /** Clinical sanity check result */
  sanityCheck: SanityCheckResult;
  /** Timestamp of injection */
  injectedAt: Date;
}

// ── Listener Health ──

export type ListenerHealthState = 'connected' | 'disconnected' | 'stale' | 'error';

export interface ListenerHealthStatus {
  state: ListenerHealthState;
  lastEventAt: Date | null;
  lastHealthCheckAt: Date | null;
  disconnectedSinceMs: number | null;
  totalEventsProcessed: number;
  duplicatesSkipped: number;
  errorsEncountered: number;
}

// ── Idempotency ──

export interface ProcessedResultRecord {
  sessionId: string;
  status: 'injected' | 'skipped_duplicate' | 'failed';
  injectedAt: Date;
  extractionStatus: ExtractionStatus;
}

// ── Metrics ──

export interface CaptainListenerMetrics {
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  errorsByType: Record<string, number>;
  accuracyOnGoldStandard: number;
}

// ── Injectable Interfaces ──

// ── Clinical Sanity Audit Event (NRS.01 D#5 → xmod_security_audit) ──

export interface ClinicalSanityAuditEntry {
  eventType: 'clinical_sanity_flag';
  patientId: string;
  sessionId: string;
  biomarkerName: string;
  value: number;
  unit: string;
  expectedRange: { min: number; max: number };
  message: string;
  timestamp: string;
}

// ── XMO.09 Analyst Review Request (NRS.01 D#5) ──

export interface AnalystReviewRequest {
  sessionId: string;
  patientId: string;
  reason: 'clinical_sanity_failure';
  failures: Array<{
    biomarkerName: string;
    value: number;
    unit: string;
    expectedRange: { min: number; max: number };
  }>;
  createdAt: string;
}

// ── XMO.09 D#3 — Correction Document (captain_results/{sessionId}/corrections/{correctionId}) ──

export interface CorrectionDocument {
  /** Unique correction ID */
  correctionId: string;
  /** Session this correction applies to */
  sessionId: string;
  /** Biomarker name to correct (canonical, matches CaptainBiomarkerResult.name) */
  biomarkerName: string;
  /** Corrected numeric value */
  correctedValue: number;
  /** Unit for the corrected value */
  correctedUnit: string;
  /** Who issued the correction (e.g., 'dr-calapai', 'analyst-review') */
  source: string;
  /** When the correction was created */
  createdAt: Date;
  /** Optional reviewer notes */
  notes?: string;
}

export interface FirestoreProvider {
  onSnapshot(
    collectionPath: string,
    filter: { field: string; value: string },
    callback: (doc: CaptainResultDocument) => void,
    errorCallback: (error: Error) => void,
  ): () => void;

  /**
   * XMO.09 D#3 c1: Subscribe to corrections subcollection for a session.
   * Path: captain_results/{sessionId}/corrections
   */
  onCorrectionsSnapshot?(
    sessionId: string,
    callback: (correction: CorrectionDocument) => void,
    errorCallback: (error: Error) => void,
  ): () => void;

  getProcessedRecord(patientId: string, sessionId: string): Promise<ProcessedResultRecord | null>;
  setProcessedRecord(patientId: string, record: ProcessedResultRecord): Promise<void>;

  /** NRS.01 D#5 c2: Set clinicalSanityFlag on the captain_results document */
  updateClinicalSanityFlag(sessionId: string, flag: boolean): Promise<void>;

  /** NRS.01 D#5 c5: Write sanity flag event to xmod_security_audit */
  logClinicalSanityAudit(entry: ClinicalSanityAuditEntry): Promise<void>;

  /** NRS.01 D#5 c2: Create XMO.09 analyst review request */
  createAnalystReviewRequest(request: AnalystReviewRequest): Promise<void>;

  /**
   * NRS.01 D#1 c2: Re-authenticate after auth token expiry.
   * Returns true if re-auth succeeded, false otherwise.
   */
  reauthenticate?(): Promise<boolean>;
}

// ── Listener Lifecycle (NRS.01 D#1) ──

/** Reasons the listener can disconnect */
export type DisconnectReason = 'session_end' | 'navigation_away' | 'timeout' | 'auth_expired' | 'manual';

/** Health alert emitted when repeated disconnections detected */
export interface ListenerHealthAlert {
  sessionId: string;
  consecutiveDisconnects: number;
  lastDisconnectAt: Date;
  reason: string;
}
